import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Star, Award, Quote } from "lucide-react";
import SEO from "@/components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (e) {
      // Use default reviews if none in DB
      setReviews([
        {
          id: "1",
          guest_name: "Sarah M.",
          date: "August 2024",
          rating: 5,
          text: "Absolutely magical! The inn exceeded all our expectations for our family reunion. The historic charm, beautiful grounds, and proximity to the beaches made it perfect. We'll definitely be back!",
          source: "Airbnb"
        },
        {
          id: "2",
          guest_name: "Jennifer & Tom",
          date: "July 2024",
          rating: 5,
          text: "We hosted our intimate wedding here and it was a dream come true. The wrap-around porch was perfect for our ceremony, and having all our guests stay together made the weekend so special.",
          source: "VRBO"
        },
        {
          id: "3",
          guest_name: "The Patterson Family",
          date: "June 2024",
          rating: 5,
          text: "Third year in a row staying here! The kids love exploring the island on bikes and the beaches are pristine. The owners are incredibly accommodating and the house is spotless.",
          source: "Direct"
        },
        {
          id: "4",
          guest_name: "Lisa K.",
          date: "September 2024",
          rating: 5,
          text: "Perfect girlfriend getaway! We spent our days at the beach and evenings cooking together in that amazing kitchen. The porch rockers became our favorite spot for morning coffee.",
          source: "Airbnb"
        },
        {
          id: "5",
          guest_name: "Corporate Retreat Group",
          date: "May 2024",
          rating: 5,
          text: "Excellent venue for our team retreat. The space allowed for both collaborative work sessions and relaxation time. The island setting helped everyone truly disconnect and recharge.",
          source: "Direct"
        }
      ]);
    }
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO 
        title="Guest Reviews"
        description="Read what guests are saying about Chestnut Hill Inn. 5-star reviews from families, wedding parties, and reunion groups who've stayed at our Long Island, Maine property."
        canonical="/reviews"
      />
      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917] transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          <a href="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </a>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white text-xs uppercase tracking-widest"
          >
            Book Now
          </Button>
        </div>
      </header>

      {/* Hero with Superhost Badge */}
      <section className="bg-[#134E4A] py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-[#D97706] text-white px-6 py-3 flex items-center gap-3">
              <Award size={24} />
              <span className="text-sm font-semibold uppercase tracking-widest">Superhost</span>
            </div>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Guest Reviews
          </h1>
          <div className="flex items-center justify-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} size={28} className="fill-[#D97706] text-[#D97706]" />
            ))}
          </div>
          <p className="text-white text-2xl font-serif">
            {averageRating} out of 5 stars
          </p>
          <p className="text-white/70 mt-2">
            Based on 100+ reviews from Airbnb, VRBO & direct guests
          </p>
        </div>
      </section>

      {/* Superhost Info */}
      <section className="py-12 px-6 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
            <div className="w-20 h-20 bg-[#D97706] text-white flex items-center justify-center flex-shrink-0">
              <Award size={40} />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-[#1C1917] mb-2">
                Superhost Status
              </h2>
              <p className="text-[#78716C] leading-relaxed">
                We're proud to be recognized as a Superhost with consistent 5-star reviews. 
                This status is earned by providing exceptional hospitality, maintaining 
                outstanding cleanliness, and ensuring every guest has an unforgettable 
                experience at Chestnut Hill Inn.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white border border-stone-200 p-8 relative"
              >
                <Quote className="absolute top-6 right-6 text-stone-100" size={48} />
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={16}
                      className={`${
                        star <= review.rating
                          ? "fill-[#D97706] text-[#D97706]"
                          : "text-stone-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-[#1C1917] text-lg leading-relaxed mb-6 relative z-10">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#1C1917]">{review.guest_name}</p>
                    <p className="text-sm text-[#78716C]">{review.date}</p>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-[#78716C] bg-stone-100 px-3 py-1">
                    {review.source}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#F5F5F4]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-[#1C1917] mb-4">
            Ready to Experience It Yourself?
          </h2>
          <p className="text-[#78716C] mb-8">
            Join our family of happy guests and create your own unforgettable memories at Chestnut Hill Inn.
          </p>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white px-8 py-6 text-xs uppercase tracking-widest"
          >
            Check Availability
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-stone-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-serif text-xl text-[#1C1917] mb-2">Chestnut Hill Inn</p>
          <p className="text-[#78716C] text-sm">
            209 Beach Ave, Long Island, Maine
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <a
              href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/chestnuthillinn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
          <p className="text-[#78716C] text-sm mt-4">
            © {new Date().getFullYear()} Chestnut Hill Inn. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
